import React, { FC, useEffect, useRef } from 'react';
import { groupBy } from 'lodash';
import FullCalendar from '@fullcalendar/react'; // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import useScheduleNavigator from 'hooks/useScheduleNavigator';
import moment from 'moment';
import useServiceScheduleException from 'hooks/useServiceScheduleException';
import { CUSTOM_HOURS, OFFICE_CLOSED, UNAVAILABLE } from 'utils/constants';
import Legend from 'components/Legend/Legend';
import { IService } from 'interfaces/services';
import { ClinicException } from 'interfaces/clinicException';

import DayCell, { CellData } from './DayCell/DayCell';
import EditScheduleModal from '../EditScheduleModal/EditScheduleModal';
import SyncExceptionModal from '../SyncExceptionModal/SyncExceptionModal';
import { DATE_TIME_FORMAT } from 'utils/datetime';

interface CalendarProps {
  service: IService;
  state: ReturnType<typeof useScheduleNavigator>['state'];
  dispatch: ReturnType<typeof useScheduleNavigator>['dispatch'];
  clinicException: ClinicException[] | undefined;
}

const Calendar: FC<CalendarProps> = ({
  service,
  state,
  dispatch,
  clinicException,
}) => {
  const calendarRef = useRef<FullCalendar | null>(null);

  const [selectedCellData, setSelectedCellData] =
    React.useState<CellData | null>(null);

  const { data: serviceScheduleExceptions } = useServiceScheduleException({
    startDate: state.monthValue,
    endDate: moment(state.monthValue).endOf('month').format('YYYY-MM-DD'),
    serviceId: service.id,
  });

  const monthValue =
    serviceScheduleExceptions && clinicException
      ? serviceScheduleExceptions.map((serviceScheduleException, index) => ({
          date: serviceScheduleException.date,
          day: serviceScheduleException.day,
          isClinicClosed: !!clinicException[index].isClosed,
          holidayName: clinicException[index].holidayName,
          isHoliday: !!clinicException[index].isHoliday,
          isServiceClosed:
            serviceScheduleException.shifts.length === 0 ||
            serviceScheduleException.shifts.every(
              (shift) => shift.startTime === null || shift.endTime === null
            ),
          isServiceException: serviceScheduleException.isException,
          shifts: serviceScheduleException.shifts,
          clinicWorkingHour: clinicException[index].workingHour,
          isSyncedException: serviceScheduleException.isSyncedException,
        }))
      : [];

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const parsedDate = state.monthValue
        ? moment(state.monthValue, 'YYYY-MM-DD').toDate()
        : new Date();

      calendarApi.gotoDate(parsedDate);
    }
  }, [state.monthValue]);

  const onCellClicked = (cellData: CellData) => {
    setSelectedCellData(cellData);
  };

  return (
    <>
      {selectedCellData &&
        (selectedCellData.isSyncedException ? (
          <SyncExceptionModal
            selectedCellData={selectedCellData as any}
            service={service}
            onClose={() => setSelectedCellData(null)}
          />
        ) : (
          <EditScheduleModal
            selectedCellData={selectedCellData}
            service={service}
            onClose={() => setSelectedCellData(null)}
          />
        ))}
      <div className="mt-2" id="schedule-calendar">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          initialDate={new Date()}
          fixedWeekCount
          showNonCurrentDates
          headerToolbar={false}
          contentHeight={650}
          dayCellClassNames={'h-[75px]'}
          dayCellContent={(props) => {
            const dayInfo =
              monthValue?.find(
                (item) => item.date === moment(props.date).format('YYYY-MM-DD')
              ) || null;

            const isExistingTwoPmsAvailability = monthValue.some((item) => {
              const groupByOpId = groupBy(item.shifts, 'operatory.id');

              return (
                Object.keys(groupByOpId).length > 2 &&
                item.isSyncedException &&
                moment(
                  item.date,
                  DATE_TIME_FORMAT.APPOINTMENT_DATE
                ).isSameOrAfter(moment(), 'days')
              );
            });

            return (
              <DayCell
                {...props}
                dayInfo={dayInfo}
                selectedDate={state.monthValue}
                onCellClicked={onCellClicked}
                highlightedDate={state.highlightedDate}
                resetHighlightedDate={() =>
                  dispatch({ type: 'resetHighLightedDate' })
                }
                isExistingTwoPmsAvailability={isExistingTwoPmsAvailability}
              />
            );
          }}
        />

        <div className="flex flex-row justify-end mt-1.6 gap-x-6">
          <Legend icon="circle">{CUSTOM_HOURS}</Legend>
          <Legend icon="square" className="before:bg-[#F4FAFF]">
            {UNAVAILABLE}
          </Legend>
          <Legend icon="square">{OFFICE_CLOSED}</Legend>
        </div>
      </div>
    </>
  );
};

export default Calendar;
